import { ChangeDetectorRef, Component, DestroyRef, effect, inject, input, OnInit, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import {
    PrestadorServicoChefeTecnicoResponse,
    PrestadorServicoDetalheResponse,
    PrestadorServicoEspecialidadeResponse,
    PrestadorServicoMedicoResponse,
    PrestadorServicoOpcaoResponse,
    PrestadorServicosEspecialidadesResponse
} from '@/core/api/sicom-api/registro/prestador/prestador-servicos-especialidades.model';
import { PrestadorServicosEspecialidadesService } from '@/core/api/sicom-api/registro/prestador/prestador-servicos-especialidades.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorFinanceiroNavigationService, SERVICOS_FINANCEIRO_RETURN_CONTEXT_ID } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-financeiro-navigation.service';
import { SelectButtonModule } from 'primeng/selectbutton';
import { BaseEditableForm } from '@/shared/forms/base-editable-form';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';
import { DateUtils } from '@/shared/utils/date-utils';
import { FormDatePicker } from '@/shared/components/layout/form-date-picker/form-date-picker';
import { EspecialidadeService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { AreaAtuacaoPorEspecialidadeResponse, EspecialidadeListaResponse } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { PrestadorComposicaoItemResponse } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.model';
import { PrestadorComposicaoService } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface ServicoDesenvolvido {
    idServico: number;
    servico: string;
    inclusao: string;
    inclusaoApi?: string | null;
    exclusao: string;
    exclusaoApi?: string | null;
    exigeRqe?: boolean;
    exigeDiretor?: boolean;
    necessitaChefeTecnico?: boolean;
    possuiChefeQualificado?: boolean;
    chefeTecnico?: ChefeTecnico | null;
    chefeTecnicoLabel?: string;
}

interface ChefeTecnico {
    idMedico?: number | null;
    idServico?: number | null;
    crm: string;
    uf: string;
    nome: string;
    entrada: string;
    entradaApi?: string | null;
    saida: string;
    saidaApi?: string | null;
}

interface ComposicaoAntigaChefe {
    idServico?: number | null;
    idMedico?: number | null;
    uf: string;
    crm: string;
    nomeMedico: TablePerfilValue;
    entrada: string;
    saida: string;
    servico: string;
}

interface Especialidade {
    idPrestadorEspecialidade: number;
    idEspecialidade: number;
    idEspecArea: number;
    especialidade: string;
    areaAtuacao: string;
    dataInicio: string;
    dataInicioApi?: string | null;
    dataFim: string;
    dataFimApi?: string | null;
    enfase?: boolean;
    enfaseLabel?: string;
}

interface ServicoDetalhe {
    idServico: number;
    servico: string;
    inclusao: string;
    exclusao: string;
    exigeRqe?: boolean;
    exigeDiretor?: boolean;
    necessitaChefeTecnico?: boolean;
    chefeTecnico?: ChefeTecnico | null;
    norma?: string | null;
    excecao?: string | null;
    especialidades: string[];
    areasAtuacao: string[];
    mensagem?: string | null;
}

@Component({
    selector: 'app-servicos-especialidades-prestador',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SelectButtonModule,
        TableModule,
        ButtonModule,
        DialogModule,
        SelectModule,
        InputTextModule,
        CheckboxModule,
        FormDatePicker,
        TabContainer,
        TabContent,
        CardContent,
        CustomTableComponent,
        BotaoAcoes
    ],
    templateUrl: './servicos-especialidades-prestador.html',
    styleUrl: './servicos-especialidades-prestador.scss'
})
export class ServicosEspecialidadesPrestador extends BaseEditableForm implements OnInit {
    pessoa = input<Pessoa>();
    protected readonly rolePrestadoresServicosEspecialidadesEditar = PERMISSOES.prestadores.servicosEspecialidades.editar;
    protected readonly EInconPos = EInconPos;

    /** Ver {@link itensAcoes}: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.rolePrestadoresServicosEspecialidadesEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: this.disableSalvar, permission: role, command: () => this.salvar() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelar() })
        ];
    }

    get itensAcoes(): AppMenuItem[] {
        const role = this.rolePrestadoresServicosEspecialidadesEditar;
        const editando = this.isEditing();
        const emServicos = this.selected === 'SERVICOS';
        return [
            acaoMenuItem({ id: 'editar', label: 'Editar', icon: 'pi pi-pencil', cor: 'editar', disabled: editando, permission: role, command: () => this.onEdicaoEnabled() }),
            // Escopado pela aba ativa: um item so, despachando para o dialog correspondente.
            acaoMenuItem({
                id: 'adicionar',
                label: emServicos ? 'Adicionar serviço' : 'Adicionar especialidade',
                icon: 'pi pi-plus',
                cor: 'novo',
                disabled: !editando,
                permission: role,
                command: () => (emServicos ? this.abrirServicoDialog() : this.abrirEspecialidadeDialog())
            })
        ];
    }

    private readonly destroyRef = inject(DestroyRef);
    private readonly fotos = inject(FotoMedicoCacheService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly financeiroNavigationService = inject(PrestadorFinanceiroNavigationService);
    modoEdicao = true;
    carregando = signal(false);

    options = [
        { label: 'Serviços', value: 'SERVICOS', icon: 'pi pi-cog' },
        { label: 'Especialidade', value: 'ESPECIALIDADE', icon: 'pi pi-graduation-cap' }
    ];
    selected: string = 'SERVICOS';

    // Dados dos serviços desenvolvidos
    servicosDesenvolvidos: ServicoDesenvolvido[] = [
        // Dados mockados para demonstração
    ];

    // Dados das especialidades
    especialidades: Especialidade[] = [
        // Dados mockados para demonstração
    ];
    especialidadesSemMedicosAtivosComRqe: Especialidade[] = [];
    especialidadesFinalizadas: Especialidade[] = [];

    // ── Configuração das tabelas padrão do sistema (app-custom-table) ──
    readonly colunasServicos = ['servico', 'chefeTecnicoLabel', 'inclusao', 'exclusao'];
    readonly colunasComposicaoAntiga = ['uf', 'crm', 'nomeMedico', 'entrada', 'saida', 'servico'];
    readonly colunasEspecialidades = ['especialidade', 'areaAtuacao', 'dataInicio', 'dataFim'];
    readonly colunasEspecialidadesEnfase = ['especialidade', 'areaAtuacao', 'dataInicio', 'dataFim', 'enfaseLabel'];

    readonly metadataServicos: TableMetadata = {
        headers: {
            servico: { type: 'text', value: 'Serviços desenvolvidos', default: '-', hidden: false },
            chefeTecnicoLabel: { type: 'text', value: 'Chefe Técnico', default: 'Sem chefe', hidden: false },
            inclusao: { type: 'text', value: 'Inclusão', default: '-', hidden: false },
            exclusao: { type: 'text', value: 'Exclusão', default: '-', hidden: false }
        },
        title: 'servico',
        actions: {
            ver: { label: 'Ver', icon: 'pi pi-eye', color: 'secondary', command: (item) => this.abrirDetalheServico(item) },
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'info', command: (item) => this.abrirServicoDialog(item), disabled: () => !this.isEditing() },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item) => this.removerServico(item), disabled: () => !this.isEditing() }
        }
    };
    readonly metadataComposicaoAntiga: TableMetadata = {
        headers: {
            uf: { type: 'text', value: 'UF', default: '-', hidden: false },
            crm: { type: 'text', value: 'CRM', default: '-', hidden: false },
            nomeMedico: { type: 'perfil', value: 'Nome do Médico', default: '-', hidden: false },
            entrada: { type: 'text', value: 'Entrada', default: '-', hidden: false },
            saida: { type: 'text', value: 'Saída', default: '-', hidden: false },
            servico: { type: 'text', value: 'Serviço', default: '-', hidden: false }
        },
        title: 'nomeMedico',
        actions: {
            informacoesFinanceiras: {
                label: 'Informações financeiras',
                icon: 'pi pi-dollar',
                color: 'primary',
                command: (item: ComposicaoAntigaChefe) => this.abrirInformacoesFinanceiras(item)
            }
        }
    };
    readonly metadataEspecialidades: TableMetadata = {
        headers: {
            especialidade: { type: 'text', value: 'Especialidade', default: '-', hidden: false },
            areaAtuacao: { type: 'text', value: 'Área de Atuação', default: '-', hidden: false },
            dataInicio: { type: 'text', value: 'Data início', default: '-', hidden: false },
            dataFim: { type: 'text', value: 'Data Fim', default: '-', hidden: false }
        },
        title: 'especialidade',
        actions: {
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'info', command: (item) => this.abrirEspecialidadeDialog(item), disabled: () => !this.isEditing() },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item) => this.removerEspecialidade(item), disabled: () => !this.isEditing() }
        }
    };
    readonly metadataEspecialidadesSemRqe: TableMetadata = {
        headers: {
            especialidade: { type: 'text', value: 'Especialidade', default: '-', hidden: false },
            areaAtuacao: { type: 'text', value: 'Área de Atuação', default: '-', hidden: false },
            dataInicio: { type: 'text', value: 'Data início', default: '-', hidden: false },
            dataFim: { type: 'text', value: 'Data Fim', default: '-', hidden: false },
            enfaseLabel: { type: 'text', value: 'Esp. Ênfase', default: '-', hidden: false }
        },
        title: 'especialidade'
    };
    readonly metadataEspecialidadesFinalizadas: TableMetadata = {
        headers: {
            especialidade: { type: 'text', value: 'Especialidade', default: '-', hidden: false },
            areaAtuacao: { type: 'text', value: 'Área de Atuação', default: '-', hidden: false },
            dataInicio: { type: 'text', value: 'Data início', default: '-', hidden: false },
            dataFim: { type: 'text', value: 'Data Fim', default: '-', hidden: false },
            enfaseLabel: { type: 'text', value: 'Esp. Ênfase', default: '-', hidden: false }
        },
        title: 'especialidade',
        actions: {
            restaurar: { label: 'Restaurar', icon: 'pi pi-refresh', color: 'secondary', command: (item) => this.abrirRestaurarEspecialidade(item), disabled: () => !this.isEditing() }
        }
    };

    // Coloração das linhas (espelha classeLinhaServico / legado): serviço sem chefe = vermelho,
    // com chefe = azul; especialidade finalizada = vermelho.
    readonly rowStyleServico = (row: ServicoDesenvolvido): Record<string, string> => {
        if (!row.necessitaChefeTecnico) return {};
        return row.chefeTecnico ? { color: 'var(--p-blue-500)' } : { color: 'var(--p-red-500)' };
    };
    readonly rowStyleFinalizada = (): Record<string, string> => ({ color: 'var(--p-red-500)' });

    chefeOriginal: ChefeTecnico | null = null;
    servicoOpcoes: PrestadorServicoOpcaoResponse[] = [];
    especialidadeOpcoes: EspecialidadeListaResponse[] = [];
    areaAtuacaoOpcoes: AreaAtuacaoPorEspecialidadeResponse[] = [];
    servicoDialogVisible = false;
    servicoDetalheDialogVisible = false;
    confirmarSubstituicaoDialogVisible = false;
    composicaoDialogVisible = false;
    especialidadeDialogVisible = false;
    restaurandoEspecialidade = false;
    servicoEmEdicao: ServicoDesenvolvido | null = null;
    especialidadeEmEdicao: Especialidade | null = null;
    servicoDialogForm!: FormGroup;
    confirmarSubstituicaoForm!: FormGroup;
    especialidadeDialogForm!: FormGroup;
    composicaoAtual: PrestadorComposicaoItemResponse[] = [];
    servicoDetalhe: ServicoDetalhe | null = null;

    // Composição antiga de chefes técnicos
    composicaoAntigaChefes: ComposicaoAntigaChefe[] = [
        // Dados mockados para demonstração
    ];

    isEditing = signal(false);

    constructor(
        private fb: FormBuilder,
        private prestadorServicosEspecialidadesService: PrestadorServicosEspecialidadesService,
        private especialidadeService: EspecialidadeService,
        private prestadorComposicaoService: PrestadorComposicaoService
    ) {
        super();

        effect(() => {
            const pessoa = this.pessoa();
            untracked(() => {
                if (pessoa?.id && pessoa.uf) {
                    this.carregarDados(pessoa);
                } else {
                    this.limparDados();
                }
            });
        });
    }

    // Desabilita o botão Salvar do header quando o form estiver inválido
    get disableSalvar(): boolean {
        return !this.form?.valid;
    }

    // Handler do header do TabContainer para entrar em modo de edição
    onEdicaoEnabled(): void {
        this.isEditing.set(true);
        this.enableEditing();
    }

    ngOnInit(): void {
        this.form = this.fb.group({});
        this.servicoDialogForm = this.fb.group({
            idServico: [null],
            inclusao: [null],
            exclusao: [null],
            chefeTecnicoIdMedico: [{ value: null, disabled: true }],
            chefeTecnicoCrm: [{ value: '', disabled: true }],
            chefeTecnicoUf: [{ value: '', disabled: true }],
            chefeTecnicoNome: [{ value: '', disabled: true }],
            chefeTecnicoEntrada: [null],
            chefeTecnicoSaida: [null]
        });
        this.confirmarSubstituicaoForm = this.fb.group({
            entradaOriginal: [null, Validators.required],
            dataSubstituicao: [new Date(), Validators.required]
        });
        this.especialidadeDialogForm = this.fb.group({
            idEspecialidade: [null],
            idEspecArea: [null],
            dataInicio: [null],
            dataFim: [null],
            enfase: [false]
        });
        this.setForm(this.form);
        this.carregarOpcoesDialogos();
    }

    /**
     * Toggle "Exibir serviços em Transição" (ckexibeTransicao do legado, UntCadPrestador.pas:752):
     * quando ligado inclui os serviços em transição (IN_VISIBILIDADE='T'); desligado, só os normais.
     */
    exibirTransicaoServicos = false;

    onExibirTransicaoChange(event: { checked?: boolean }): void {
        // Usa o valor definitivo do evento (o [(ngModel)] pode ainda não ter propagado no momento
        // do onChange), evitando carregar com o estado anterior do checkbox.
        this.exibirTransicaoServicos = !!event?.checked;
        this.carregarDados();
    }

    carregarDados(pessoa: Pessoa = this.pessoa()!): void {
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }

        this.carregando.set(true);
        this.prestadorServicosEspecialidadesService
            .buscar(pessoa, this.exibirTransicaoServicos)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (response) => this.aplicarResponse(response),
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.limparDados()
            });
    }

    aplicarResponse(response: PrestadorServicosEspecialidadesResponse): void {
        this.servicosDesenvolvidos = (response.servicos ?? []).map((servico) => {
            const chefeTecnico = this.toChefeTecnico(servico.chefeTecnico);
            return {
                idServico: servico.idServico,
                servico: servico.servico,
                inclusao: this.formatarData(servico.inclusao),
                inclusaoApi: servico.inclusao,
                exclusao: this.formatarData(servico.exclusao),
                exclusaoApi: servico.exclusao,
                exigeRqe: servico.exigeRqe,
                exigeDiretor: servico.exigeDiretor,
                necessitaChefeTecnico: servico.necessitaChefeTecnico,
                possuiChefeQualificado: servico.possuiChefeQualificado,
                chefeTecnico,
                chefeTecnicoLabel: chefeTecnico ? `${chefeTecnico.nome} — CRM ${chefeTecnico.crm}/${chefeTecnico.uf}` : 'Sem chefe'
            };
        });

        this.especialidades = (response.especialidades ?? []).map((especialidade) => this.toEspecialidade(especialidade));
        this.especialidadesSemMedicosAtivosComRqe = (response.especialidadesSemMedicosAtivosComRqe ?? []).map((especialidade) => this.toEspecialidade(especialidade));
        this.especialidadesFinalizadas = (response.especialidadesFinalizadas ?? []).map((especialidade) => this.toEspecialidade(especialidade));

        const chefesAntigos = response.chefesTecnicosAntigos ?? [];
        this.composicaoAntigaChefes = chefesAntigos.map((chefe) => ({
            idServico: chefe.idServico,
            idMedico: chefe.idMedico ?? null,
            uf: chefe.ufMedico ?? '',
            crm: chefe.crm?.toString() ?? '',
            nomeMedico: this.fotos.perfilMedico(chefe.nome, chefe.crm, chefe.ufMedico),
            entrada: this.formatarData(chefe.entrada),
            saida: this.formatarData(chefe.saida),
            servico: chefe.servico ?? ''
        }));
        // Diferente das outras tabelas do módulo, aqui as linhas são um array comum, não `computed`:
        // a foto que chega depois precisa reescrever a linha, senão só apareceria na próxima carga.
        this.fotos
            .precarregar(chefesAntigos.map((chefe) => ({ crm: chefe.crm, uf: chefe.ufMedico })))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.composicaoAntigaChefes = this.composicaoAntigaChefes.map((chefe) => ({
                    ...chefe,
                    nomeMedico: this.fotos.perfilMedico(chefe.nomeMedico.nome, chefe.crm, chefe.uf)
                }));
                this.cdr.markForCheck();
            });

        this.disableEditing();
        // A resposta chega em um tick assincrono; marca a arvore (ancestrais OnPush) para
        // re-renderizar sem depender de uma interacao do usuario (ex.: toggle de transicao).
        this.cdr.markForCheck();
    }

    limparDados(): void {
        this.servicosDesenvolvidos = [];
        this.especialidades = [];
        this.especialidadesSemMedicosAtivosComRqe = [];
        this.especialidadesFinalizadas = [];
        this.composicaoAntigaChefes = [];
        this.form?.reset();
        this.disableEditing();
    }

    toChefeTecnico(chefe?: PrestadorServicoChefeTecnicoResponse | null): ChefeTecnico | null {
        if (!chefe) {
            return null;
        }

        return {
            idMedico: chefe.idMedico,
            idServico: chefe.idServico,
            crm: chefe.crm?.toString() ?? '',
            uf: chefe.ufMedico ?? '',
            nome: chefe.nome ?? '',
            entrada: this.formatarData(chefe.entrada),
            entradaApi: chefe.entrada,
            saida: this.formatarData(chefe.saida),
            saidaApi: chefe.saida
        };
    }

    toEspecialidade(especialidade: PrestadorServicoEspecialidadeResponse): Especialidade {
        return {
            idPrestadorEspecialidade: especialidade.idPrestadorEspecialidade,
            idEspecialidade: especialidade.idEspecialidade,
            idEspecArea: especialidade.idEspecArea,
            especialidade: especialidade.especialidade,
            areaAtuacao: especialidade.areaAtuacao ?? '',
            dataInicio: this.formatarData(especialidade.dataInicio),
            dataInicioApi: especialidade.dataInicio,
            dataFim: this.formatarData(especialidade.dataFim),
            dataFimApi: especialidade.dataFim,
            enfase: especialidade.enfase,
            enfaseLabel: especialidade.enfase ? 'Sim' : 'Não'
        };
    }

    toServicoDetalhe(detalhe: PrestadorServicoDetalheResponse): ServicoDetalhe {
        return {
            idServico: detalhe.idServico,
            servico: detalhe.servico,
            inclusao: this.formatarData(detalhe.inclusao),
            exclusao: this.formatarData(detalhe.exclusao),
            exigeRqe: detalhe.exigeRqe,
            exigeDiretor: detalhe.exigeDiretor,
            necessitaChefeTecnico: detalhe.necessitaChefeTecnico,
            chefeTecnico: this.toChefeTecnico(detalhe.chefeTecnico),
            norma: detalhe.norma,
            excecao: detalhe.excecao,
            especialidades: detalhe.especialidades ?? [],
            areasAtuacao: detalhe.areasAtuacao ?? [],
            mensagem: detalhe.mensagem
        };
    }

    formatarData(value?: string | null): string {
        return value ? DateUtils.formatDate(value) : '';
    }

    abrirInformacoesFinanceiras(chefe: ComposicaoAntigaChefe): void {
        if (!chefe.idMedico) {
            return;
        }
        this.financeiroNavigationService.abrirMedico(
            this.pessoa() as PessoaDomain | undefined,
            { id: chefe.idMedico, uf: chefe.uf, crm: chefe.crm || undefined, nome: chefe.nomeMedico?.nome || undefined, tipo: 'MEDICO' },
            { id: SERVICOS_FINANCEIRO_RETURN_CONTEXT_ID, returnUrl: '/prestadores/servicos-especialidades' }
        );
    }

    classeLinhaServico(servico: ServicoDesenvolvido): string {
        if (!servico.necessitaChefeTecnico) {
            return '';
        }
        return servico.chefeTecnico ? 'servico-com-chefe' : 'servico-sem-chefe';
    }
    salvar(): void {
        this.modoEdicao = false;
        this.isEditing.set(false);
        this.disableEditing();
    }

    cancelar(): void {
        this.form.reset();
        this.modoEdicao = false;
        this.isEditing.set(false);
        this.disableEditing();
    }

    private bloquearCamposMedicoChefe(): void {
        this.servicoDialogForm.get('chefeTecnicoIdMedico')?.disable();
        this.servicoDialogForm.get('chefeTecnicoCrm')?.disable();
        this.servicoDialogForm.get('chefeTecnicoUf')?.disable();
        this.servicoDialogForm.get('chefeTecnicoNome')?.disable();
    }

    private aplicarBloqueioDadosServico(servico?: ServicoDesenvolvido): void {
        const controles = ['idServico', 'inclusao', 'exclusao'];
        const bloquear = !!servico && this.temChefiaCadastrada(servico);
        controles.forEach((controle) => this.servicoDialogForm.get(controle)?.[bloquear ? 'disable' : 'enable']());
    }

    private temChefiaCadastrada(servico: ServicoDesenvolvido): boolean {
        return !!servico.chefeTecnico || this.composicaoAntigaChefes.some((chefe) => chefe.idServico === servico.idServico);
    }

    abrirServicoDialog(servico?: ServicoDesenvolvido): void {
        this.servicoEmEdicao = servico ?? null;
        this.chefeOriginal = servico?.chefeTecnico ? { ...servico.chefeTecnico } : null;
        this.servicoDialogForm.reset({
            idServico: servico?.idServico ?? null,
            inclusao: DateUtils.parseToDate(servico?.inclusaoApi),
            exclusao: DateUtils.parseToDate(servico?.exclusaoApi),
            chefeTecnicoIdMedico: servico?.chefeTecnico?.idMedico ?? null,
            chefeTecnicoCrm: servico?.chefeTecnico?.crm ?? '',
            chefeTecnicoUf: servico?.chefeTecnico?.uf ?? '',
            chefeTecnicoNome: servico?.chefeTecnico?.nome ?? '',
            chefeTecnicoEntrada: DateUtils.parseToDate(servico?.chefeTecnico?.entradaApi),
            chefeTecnicoSaida: DateUtils.parseToDate(servico?.chefeTecnico?.saidaApi)
        });
        this.aplicarBloqueioDadosServico(servico);
        this.bloquearCamposMedicoChefe();
        this.servicoDialogVisible = true;
    }

    abrirDetalheServico(servico: ServicoDesenvolvido): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.prestadorServicosEspecialidadesService
            .buscarDetalheServico(pessoa, servico.idServico)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.servicoDetalhe = this.toServicoDetalhe(response);
                this.servicoDetalheDialogVisible = true;
                // A resposta chega em um tick assíncrono; marca a árvore (ancestrais OnPush) para
                // que o diálogo apareça sem depender de uma interação do usuário.
                this.cdr.markForCheck();
            });
    }

    salvarServicoDialog(substituicaoConfirmada = false): void {
        const pessoa = this.pessoa();
        const raw = this.servicoDialogForm.getRawValue();
        if (!pessoa?.id || !pessoa.uf || !raw.idServico) {
            return;
        }
        if (this.precisaConfirmarSubstituicao(raw) && !substituicaoConfirmada) {
            this.abrirConfirmacaoSubstituicao();
            return;
        }

        const serviceRequest = {
            id: Number(pessoa.id),
            uf: pessoa.uf,
            idServico: Number(raw.idServico),
            idServicoOriginal: this.servicoEmEdicao?.idServico ?? null,
            inclusao: DateUtils.formatDateToApi(raw.inclusao),
            inclusaoOriginal: this.servicoEmEdicao?.inclusaoApi ?? null,
            exclusao: DateUtils.formatDateToApi(raw.exclusao)
        };
        const servicoSemAlteracao = this.servicoEmEdicao && serviceRequest.idServico === this.servicoEmEdicao.idServico && serviceRequest.inclusao === this.servicoEmEdicao.inclusaoApi && serviceRequest.exclusao === this.servicoEmEdicao.exclusaoApi;

        const salvarServico$: Observable<PrestadorServicosEspecialidadesResponse | null> = servicoSemAlteracao ? of(null) : this.prestadorServicosEspecialidadesService.salvarServico(serviceRequest);

        salvarServico$
            .pipe(
                switchMap((response) => {
                    if (!raw.chefeTecnicoIdMedico) {
                        return response ? of(response) : this.prestadorServicosEspecialidadesService.buscar(pessoa);
                    }
                    return this.prestadorServicosEspecialidadesService.salvarChefeTecnico({
                        id: Number(pessoa.id),
                        uf: pessoa.uf,
                        idServico: Number(raw.idServico),
                        idMedico: Number(raw.chefeTecnicoIdMedico),
                        idServicoOriginal: this.chefeOriginal?.idServico ?? this.servicoEmEdicao?.idServico ?? null,
                        idMedicoOriginal: this.chefeOriginal?.idMedico ?? null,
                        entrada: DateUtils.formatDateToApi(raw.chefeTecnicoEntrada),
                        entradaOriginal: this.chefeOriginal?.entradaApi ?? null,
                        dataSubstituicao: this.dataSubstituicaoConfirmada(),
                        saida: DateUtils.formatDateToApi(raw.chefeTecnicoSaida)
                    });
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (response) => {
                    this.aplicarResponse(response);
                    this.servicoDialogVisible = false;
                },
                // Cadeia de duas escritas: se `salvarChefeTecnico` falha, `salvarServico` JÁ gravou.
                // Sem recarregar, a lista continuaria mostrando o serviço antigo e o usuário veria
                // só o toast, concluindo que nada foi salvo. O diálogo fica aberto para ele corrigir
                // o chefe — `aplicarResponse` mexe nas listas, não no formulário do diálogo.
                error: () => this.carregarDados()
            });
    }

    precisaConfirmarSubstituicao(raw: { chefeTecnicoIdMedico?: number | string | null }): boolean {
        return !!this.chefeOriginal?.idMedico && !!raw.chefeTecnicoIdMedico && Number(raw.chefeTecnicoIdMedico) !== this.chefeOriginal.idMedico;
    }

    abrirConfirmacaoSubstituicao(): void {
        this.confirmarSubstituicaoForm.reset({
            entradaOriginal: DateUtils.parseToDate(this.chefeOriginal?.entradaApi),
            dataSubstituicao: new Date()
        });
        this.confirmarSubstituicaoDialogVisible = true;
    }

    confirmarSubstituicao(): void {
        if (this.confirmarSubstituicaoForm.invalid || !this.datasSubstituicaoValidas()) {
            this.confirmarSubstituicaoForm.markAllAsTouched();
            return;
        }
        const entradaOriginal = DateUtils.formatDateToApi(this.confirmarSubstituicaoForm.getRawValue().entradaOriginal);
        this.chefeOriginal = this.chefeOriginal ? { ...this.chefeOriginal, entradaApi: entradaOriginal ?? this.chefeOriginal.entradaApi } : null;
        this.confirmarSubstituicaoDialogVisible = false;
        this.salvarServicoDialog(true);
    }

    datasSubstituicaoValidas(): boolean {
        const raw = this.confirmarSubstituicaoForm.getRawValue();
        if (!raw.entradaOriginal || !raw.dataSubstituicao) {
            return false;
        }
        return DateUtils.formatDateToApi(raw.entradaOriginal)! <= DateUtils.formatDateToApi(raw.dataSubstituicao)!;
    }

    dataSubstituicaoConfirmada(): string | null {
        return DateUtils.formatDateToApi(this.confirmarSubstituicaoForm.getRawValue().dataSubstituicao);
    }

    abrirComposicaoDialog(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.prestadorComposicaoService
            .buscar(pessoa, 'ATUAL')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.composicaoAtual = response.itens ?? [];
                this.composicaoDialogVisible = true;
            });
    }

    get composicaoDisponivel(): PrestadorComposicaoItemResponse[] {
        const idServico = Number(this.servicoDialogForm?.getRawValue().idServico);
        return idServico === 46 ? this.composicaoAtual.filter((item) => item.emergencia) : this.composicaoAtual;
    }

    selecionarChefeComposicao(item: PrestadorComposicaoItemResponse): void {
        const raw = this.servicoDialogForm.getRawValue();
        this.servicoDialogForm.patchValue({
            chefeTecnicoIdMedico: item.idMedico,
            chefeTecnicoCrm: item.crm?.toString() ?? '',
            chefeTecnicoUf: item.ufMedico,
            chefeTecnicoNome: item.nome ?? '',
            chefeTecnicoEntrada: this.chefeOriginal ? new Date() : (raw.chefeTecnicoEntrada ?? new Date()),
            chefeTecnicoSaida: null
        });
        this.bloquearCamposMedicoChefe();
        this.composicaoDialogVisible = false;
    }

    removerChefeServico(): void {
        const pessoa = this.pessoa();
        const chefe = this.chefeOriginal;
        if (!pessoa?.id || !pessoa.uf || !chefe?.idServico || !chefe.idMedico) {
            this.servicoDialogForm.patchValue({
                chefeTecnicoIdMedico: null,
                chefeTecnicoCrm: '',
                chefeTecnicoUf: '',
                chefeTecnicoNome: '',
                chefeTecnicoEntrada: null,
                chefeTecnicoSaida: null
            });
            this.chefeOriginal = null;
            return;
        }

        this.prestadorServicosEspecialidadesService
            .removerChefeTecnico({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                idServico: chefe.idServico,
                idMedico: chefe.idMedico,
                entrada: chefe.entradaApi
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.aplicarResponse(response);
                this.servicoDialogVisible = false;
            });
    }

    removerServico(servico: ServicoDesenvolvido): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }

        this.prestadorServicosEspecialidadesService
            .removerServico({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                idServico: servico.idServico,
                inclusao: servico.inclusaoApi
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => this.aplicarResponse(response));
    }

    abrirEspecialidadeDialog(especialidade?: Especialidade): void {
        this.restaurandoEspecialidade = false;
        this.especialidadeEmEdicao = especialidade ?? null;
        this.especialidadeDialogForm.reset({
            idEspecialidade: especialidade?.idEspecialidade ?? null,
            idEspecArea: especialidade?.idEspecArea ?? null,
            dataInicio: DateUtils.parseToDate(especialidade?.dataInicioApi),
            dataFim: DateUtils.parseToDate(especialidade?.dataFimApi),
            enfase: especialidade?.enfase ?? false
        });
        this.especialidadeDialogForm.get('idEspecialidade')?.enable();
        this.especialidadeDialogForm.get('idEspecArea')?.enable();
        this.areaAtuacaoOpcoes = [];
        if (especialidade?.idEspecialidade) {
            this.carregarAreasAtuacao(especialidade.idEspecialidade);
        }
        this.especialidadeDialogVisible = true;
    }

    // Restaura (reativa) uma especialidade finalizada: reabre o dialogo pre-preenchido com o registro finalizado
    // (mantendo idPrestadorEspecialidade -> reativacao implicita na API) e solicita uma nova data de inicio.
    abrirRestaurarEspecialidade(especialidade: Especialidade): void {
        this.restaurandoEspecialidade = true;
        this.especialidadeEmEdicao = especialidade;
        this.especialidadeDialogForm.reset({
            idEspecialidade: especialidade.idEspecialidade,
            idEspecArea: especialidade.idEspecArea,
            dataInicio: null,
            dataFim: null,
            enfase: especialidade.enfase ?? false
        });
        this.especialidadeDialogForm.get('idEspecialidade')?.disable();
        this.especialidadeDialogForm.get('idEspecArea')?.disable();
        this.areaAtuacaoOpcoes = [];
        if (especialidade.idEspecialidade) {
            this.carregarAreasAtuacao(especialidade.idEspecialidade);
        }
        this.especialidadeDialogVisible = true;
    }

    onEspecialidadeChange(idEspecialidade: number): void {
        this.especialidadeDialogForm.patchValue({ idEspecArea: null });
        this.carregarAreasAtuacao(idEspecialidade);
    }

    salvarEspecialidadeDialog(): void {
        const pessoa = this.pessoa();
        const raw = this.especialidadeDialogForm.getRawValue();
        if (!pessoa?.id || !pessoa.uf || !raw.idEspecArea || !raw.dataInicio) {
            return;
        }

        this.prestadorServicosEspecialidadesService
            .salvarEspecialidade({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                idPrestadorEspecialidade: this.especialidadeEmEdicao?.idPrestadorEspecialidade ?? null,
                idEspecArea: Number(raw.idEspecArea),
                dataInicio: DateUtils.formatDateToApi(raw.dataInicio),
                dataFim: DateUtils.formatDateToApi(raw.dataFim),
                enfase: Boolean(raw.enfase)
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.aplicarResponse(response);
                    this.especialidadeDialogVisible = false;
                    this.restaurandoEspecialidade = false;
                },
                // Os erros de negocio (400/409) sao exibidos automaticamente pelo interceptor global (toast);
                // mantemos o dialogo aberto para o usuario corrigir os dados.
                error: () => {}
            });
    }

    removerEspecialidade(especialidade: Especialidade): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }

        this.prestadorServicosEspecialidadesService
            .removerEspecialidade({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                idPrestadorEspecialidade: especialidade.idPrestadorEspecialidade,
                idEspecArea: especialidade.idEspecArea,
                dataInicio: especialidade.dataInicioApi
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => this.aplicarResponse(response));
    }

    private carregarOpcoesDialogos(): void {
        this.prestadorServicosEspecialidadesService
            .listarServicos()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((servicos) => (this.servicoOpcoes = servicos));
        this.especialidadeService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((especialidades) => (this.especialidadeOpcoes = especialidades));
    }

    private carregarAreasAtuacao(idEspecialidade: number): void {
        this.especialidadeService
            .listarAreasAtuacaoPorEspecialidade(idEspecialidade)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((areas) => (this.areaAtuacaoOpcoes = areas));
    }
}
